import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * RegisterSession : famille d'entités "période de caisse".
 *
 * Une période de caisse ouverte puis clôturée sur un point d'encaissement : qui
 * a ouvert, avec quel fond, ce qui est entré, ce qui a été compté, l'écart
 * constaté. Construite sur le modèle "famille + type" de la bible (patron :
 * `asset`). Le socle ne connaît pas de caisse concrète : le vocabulaire métier
 * ("main", "bar", "terrasse") est porté par le champ `type`, et l'état du cycle
 * de vie (ouverte, clôturée) par `status`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `placeId` (le point d'encaissement) est le CŒUR de la famille, donc requis ;
 * `openedByWorkerId`, `paymentIds` (les encaissements de la période) et
 * `incidentId` sont optionnels.
 *
 * Sécurité : les montants de caisse (fond, attendu, compté, écart) ne sont PAS
 * org-wide (accès rôles caisse / direction). Ils ne figurent donc PAS dans ce
 * document org-wide en v0 ; ils iront dans un sous-document privé (cf. WIP).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille registerSession (hors champs standard du socle). */
export const registerSessionCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "main", "bar"). */
  type: z.string().min(1),
  /** Point d'encaissement de la période (référence `place`), cœur de la famille. */
  placeId: z.string().min(1),
  /** Travailleur qui a ouvert la caisse (référence `worker`), optionnel. */
  openedByWorkerId: z.string().min(1).optional(),
  /** Ouverture de la période de caisse, optionnel. */
  openedAt: z.date().optional(),
  /** Clôture de la période de caisse, optionnel. */
  closedAt: z.date().optional(),
  /** État du cycle de vie, chaîne libre pilotée par l'app (ex. "open", "closed"). */
  status: z.string().min(1).optional(),
  // openingFloat : sensible (montant de caisse, non org-wide), ira dans un sous-document privé (cf. WIP), hors v0
  // expectedTotal : sensible (montant de caisse, non org-wide), ira dans un sous-document privé (cf. WIP), hors v0
  // countedTotal : sensible (montant de caisse, non org-wide), ira dans un sous-document privé (cf. WIP), hors v0
  // variance : sensible (montant de caisse, non org-wide), ira dans un sous-document privé (cf. WIP), hors v0
  /** Paiements encaissés sur la période (références `payment`), optionnel. */
  paymentIds: z.array(z.string()).optional(),
  /** Incident éventuel rattaché à la période (référence `incident`), optionnel. */
  incidentId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille registerSession (champs standard + champs métier). */
export const RegisterSessionSchema = z.object({
  ...baseEntityFields,
  ...registerSessionCoreFields,
});

/** Type d'une registerSession. */
export type RegisterSession = z.infer<typeof RegisterSessionSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const RegisterSessionCreateInputSchema = z.object({
  ...registerSessionCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une registerSession. */
export type RegisterSessionCreateInput = z.infer<typeof RegisterSessionCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const RegisterSessionUpdateInputSchema = z
  .object({ ...registerSessionCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une registerSession. */
export type RegisterSessionUpdateInput = z.infer<typeof RegisterSessionUpdateInputSchema>;
